const PREFIX = '/api/admin/observability';
const VIEWS = ['summary', 'timeseries', 'endpoints', 'companies', 'requests', 'trace', 'tasks', 'outbound'];
const ACTIVE = ['scheduled', 'running'];
const canObserve = level => Number(level) === 4;

const routeKey = row => JSON.stringify([row.method, row.route]);

function displayRouteAlias(row, catalog) {
    if (!row || typeof row.route !== 'string' || !row.route.trim() || row.eventType && row.eventType !== 'api_request') return '—';
    const entry = catalog && catalog.get(routeKey(row));
    const alias = entry ? entry.alias : row.routeAlias;
    return typeof alias === 'string' && alias.trim() ? alias : '별칭 없음';
}

function prefixForClient(client) {
    const baseURL = client && client.defaults && client.defaults.baseURL;
    if (typeof baseURL !== 'string') return PREFIX;
    try {
        const path = new URL(baseURL, 'https://observability.invalid').pathname;
        return /(?:^|\/)api\/*$/.test(path) ? '/admin/observability' : PREFIX;
    } catch (_) { return PREFIX; }
}

function rollingWindow(form, end = new Date()) {
    const duration = new Date(form.endAt) - new Date(form.startAt);
    if (!Number.isFinite(duration) || duration <= 0) throw new Error('시작 시각보다 늦은 종료 시각을 선택하세요.');
    return { ...form, startAt: new Date(end.getTime() - duration).toISOString(), endAt: end.toISOString() };
}

function buildQuery(view, form, maxWindowHours = 24) {
    if (!VIEWS.includes(view)) throw new Error('지원하지 않는 조회입니다.');
    const start = new Date(form.startAt); const end = new Date(form.endAt);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
        throw new Error('시작 시각보다 늦은 종료 시각을 선택하세요.');
    }
    if (end - start > Math.min(24, maxWindowHours) * 3600000) throw new Error(`조회 기간은 최대 ${Math.min(24, maxWindowHours)}시간입니다.`);
    const filters = {};
    for (const key of ['route', 'method', 'statusGroup', 'actorCompanyId', 'targetCompanyId', 'traceId', 'traffic']) {
        if (['trace', 'tasks', 'outbound'].includes(view) && ['route', 'method', 'statusGroup', 'traffic'].includes(key)) continue;
        const value = String(form[key] == null ? '' : form[key]).trim();
        if (!value) continue;
        if (key.endsWith('CompanyId')) {
            if (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value))) throw new Error('회사 ID는 1 이상의 숫자로 입력하세요.');
            filters[key] = Number(value);
        } else filters[key] = value;
    }
    let bucketMinutes = Number(form.bucketMinutes || 5);
    if (![5, 60].includes(bucketMinutes)) throw new Error('시간 구간은 5분 또는 1시간입니다.');
    if (end - start > 8 * 3600000) bucketMinutes = 60;
    return { view, startAt: start.toISOString(), endAt: end.toISOString(), filters, bucketMinutes };
}

function unwrap(response) {
    const envelope = response && response.data;
    if (!envelope || envelope.success !== true || !envelope.data || Array.isArray(envelope.data)) {
        throw new Error(envelope && envelope.message || '조회 응답을 읽을 수 없습니다. 잠시 후 다시 조회하세요.');
    }
    return envelope.data;
}

const visibleResult = state => state && state.status === 'complete' ? state.result : null;

function utcDate(value) {
    const text = String(value);
    return new Date(/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(text) ? text.replace(' ', 'T') + 'Z' : value);
}

function taskOutcome(row, rows) {
    if (row.outcome) return row.outcome;
    if (row.eventType !== 'task_execution') return null;
    if (row.phase === 'start' && row.executionId && rows.some(other => other.executionId === row.executionId && other.phase === 'finish')) return '종료 기록 있음';
    return '종료 미확인';
}

function timeBuckets(rows, window, minutes) {
    const size = minutes * 60000;
    const start = new Date(window.startAt).getTime(); const end = new Date(window.endAt).getTime();
    const byTime = new Map(rows.map(row => [utcDate(row.timestamp).getTime(), row]));
    const result = [];
    for (let at = Math.floor(start / size) * size; at < end; at += size) {
        const row = byTime.get(at);
        result.push({ timestamp: new Date(at).toISOString(), requests: row ? row.requests : 0,
            serverErrors: row ? row.serverErrors : 0, p95Ms: row && row.p95Ms != null ? row.p95Ms : null });
    }
    return result;
}

function errorMessage(error) {
    const response = error && error.response;
    if (response && response.status === 403) return 'API 이용 현황을 볼 권한이 없습니다. 운영자 권한을 확인하세요.';
    if (response && [404, 410].includes(response.status)) return '조회가 만료되었거나 존재하지 않습니다. 다시 조회하세요.';
    if (response && response.status === 429) {
        const seconds = Number(response.headers && response.headers['retry-after']) || 60;
        return `조회 요청이 많아 잠시 기다려야 합니다. ${seconds}초 후 다시 조회하세요.`;
    }
    return response && response.data && response.data.message || error && error.message || '조회에 실패했습니다. 다시 시도하세요.';
}

// Two workers share the start budget; reset invalidates in-flight responses before cancelling their handles.
function createQueryRunner(client, onState, options = {}) {
    const prefix = prefixForClient(client);
    const now = options.now || Date.now;
    const setTimer = options.setTimer || setTimeout; const clearTimer = options.clearTimer || clearTimeout;
    let generation = 0; let disposed = false; let rateTimer = null;
    const queue = []; const jobs = new Set(); const starts = []; const requested = new Set();
    function publish(job, state) { if (!disposed && job.generation === generation) onState(job.body.view, state); }
    function cancel(job) {
        job.cancelled = true;
        if (job.wake) job.wake();
        if (job.handle && !job.cancelSent) {
            job.cancelSent = true;
            job.cancelPromise = Promise.resolve().then(() => client.delete(`${prefix}/queries/${encodeURIComponent(job.handle)}`, { timeout: 10000 })).catch(() => {});
        }
    }
    function wait(job, ms) {
        return new Promise(resolve => {
            const id = setTimer(() => { job.wake = null; resolve(); }, ms);
            job.wake = () => { clearTimer(id); job.wake = null; resolve(); };
        });
    }
    async function run(job) {
        const started = now();
        try {
            let state = unwrap(await client.post(`${prefix}/queries`, job.body, { timeout: 15000 }));
            job.handle = state.handle;
            if (job.cancelled || job.generation !== generation || disposed) { cancel(job); return; }
            while (true) {
                if (!state.handle || state.view !== job.body.view || ![...ACTIVE, 'complete', 'failed', 'cancelled', 'timeout'].includes(state.status)) {
                    throw new Error('조회 진행 상태를 확인할 수 없습니다. 다시 조회하세요.');
                }
                if (state.expiresAt && new Date(state.expiresAt).getTime() <= now()) {
                    cancel(job); throw new Error('조회가 만료되었습니다. 다시 조회하세요.');
                }
                publish(job, state);
                if (!ACTIVE.includes(state.status)) break;
                await wait(job, Math.max(2000, Number(state.pollAfterMs) || 2000));
                if (job.cancelled || job.generation !== generation || disposed) return;
                if (now() - started >= 60000) {
                    cancel(job); publish(job, { ...state, status: 'timeout', result: null }); return;
                }
                state = unwrap(await client.get(`${prefix}/queries/${encodeURIComponent(job.handle)}`, { timeout: 15000 }));
                if (job.cancelled || job.generation !== generation || disposed) return;
            }
        } catch (error) {
            if (job.handle) cancel(job);
            publish(job, { status: 'failed', message: errorMessage(error), result: null });
        } finally {
            if (job.cancelPromise) await job.cancelPromise;
            jobs.delete(job); drain();
        }
    }
    function drain() {
        if (disposed) return;
        while (starts.length && now() - starts[0] >= 60000) starts.shift();
        while (jobs.size < 2 && queue.length) {
            if (starts.length >= 6) {
                if (rateTimer == null) rateTimer = setTimer(() => { rateTimer = null; drain(); }, Math.max(1, 60000 - (now() - starts[0])));
                return;
            }
            const job = queue.shift(); starts.push(now()); jobs.add(job); run(job);
        }
    }
    function reset() {
        generation++; queue.length = 0; requested.clear();
        if (rateTimer != null) { clearTimer(rateTimer); rateTimer = null; }
        jobs.forEach(cancel);
    }
    return {
        enqueue(body) {
            if (disposed || requested.has(body.view)) return;
            requested.add(body.view);
            const job = { body, generation, handle: null, cancelled: false };
            publish(job, { status: 'queued', result: null }); queue.push(job); drain();
        },
        reset,
        dispose() { reset(); disposed = true; },
    };
}

module.exports = { PREFIX, VIEWS, canObserve, routeKey, displayRouteAlias, prefixForClient, buildQuery, rollingWindow, unwrap, visibleResult, utcDate, timeBuckets, taskOutcome, errorMessage, createQueryRunner };
